import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { toast } from "@/lib/toast";
import { uploadFile } from "@/lib/upload";
import { authClient } from "@/auth/client";
import { authErrorMessage } from "@/lib/auth-error";
import { CircleNotch, Camera, Trash, Check } from "@phosphor-icons/react";

interface AccountFormProps {
  user: {
    id: string;
    name: string;
    email: string;
    image?: string | null;
  };
}

export function AccountForm({ user }: AccountFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState(user.name ?? "");
  const [image, setImage] = useState<string | null>(user.image ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const dirty = name !== (user.name ?? "") || image !== (user.image ?? null);
  const initial = (name || user.email || "?").trim().charAt(0).toUpperCase();

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const result = await uploadFile(file, "profiles");
      setImage(result.fileUrl);
      toast.success(t("profile.account.avatarUploaded"));
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("profile.account.avatarError")
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error(t("profile.account.nameRequired"));
      return;
    }
    setIsSaving(true);
    try {
      // Šalju se samo promijenjena polja; uklonjena slika ide kao null, jer
      // better-auth `undefined` tumači kao "ne diraj" i slika bi ostala.
      const changes: { name?: string; image?: string | null } = {};
      if (trimmedName !== (user.name ?? "")) changes.name = trimmedName;
      if (image !== (user.image ?? null)) changes.image = image;
      if (Object.keys(changes).length === 0) return;
      const { error } = await authClient.updateUser(changes);
      if (error) {
        toast.error(authErrorMessage(error, t, "profile.account.saveError"));
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
      toast.success(t("profile.account.saveSuccess"));
    } catch {
      toast.error(t("profile.account.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <WarmCard>
        <div className="space-y-8 p-6 sm:p-8">
          {/* Avatar block */}
          <section className="flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:gap-6">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="group relative shrink-0 rounded-full"
              aria-label={t("profile.account.uploadAvatar")}
            >
              <span
                className="relative flex size-20 items-center justify-center overflow-hidden rounded-full bg-white transition-shadow duration-300 group-hover:shadow-md sm:size-24"
                style={{
                  boxShadow: "0 0 0 1px var(--color-warm-border)",
                }}
              >
                {image ? (
                  <img src={image} alt="" className="size-full object-cover" />
                ) : (
                  <span
                    className="flex size-full items-center justify-center text-2xl font-semibold"
                    style={{
                      background:
                        "linear-gradient(135deg, var(--color-brand-bg), oklch(49.83% 0.0663 186.62 / 0.15))",
                      color: "var(--color-brand-dark)",
                      fontFamily: "var(--font-heading)",
                    }}
                  >
                    {initial}
                  </span>
                )}
                <span
                  className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                  style={{ background: "oklch(0% 0 0 / 0.4)" }}
                >
                  {isUploading ? (
                    <CircleNotch className="size-5 animate-spin text-white" />
                  ) : (
                    <Camera className="size-5 text-white" />
                  )}
                </span>
              </span>
            </button>

            <div className="space-y-1.5">
              <div
                className="text-xs font-semibold uppercase tracking-[0.12em]"
                style={{ color: "var(--color-brand-muted)" }}
              >
                {t("profile.account.avatar")}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium transition-all hover:scale-[1.02] disabled:opacity-50"
                  style={{
                    background: "var(--color-brand-bg-light)",
                    color: "var(--color-brand-dark)",
                    border: "1px solid var(--color-brand-bg)",
                  }}
                >
                  {isUploading ? (
                    <CircleNotch className="size-3.5 animate-spin" />
                  ) : (
                    <Camera className="size-3.5" />
                  )}
                  {t("profile.account.uploadAvatar")}
                </button>
                {image && (
                  <button
                    type="button"
                    onClick={() => setImage(null)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash className="size-3.5" />
                    {t("profile.account.removeAvatar")}
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>
          </section>

          <Divider />

          {/* Fields */}
          <section className="grid gap-5 sm:grid-cols-2">
            <FieldWrap
              label={t("profile.account.name")}
              htmlFor="name"
              className="sm:col-span-2"
            >
              <WarmInput
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={200}
                autoComplete="name"
              />
            </FieldWrap>

            <FieldWrap
              label={t("profile.account.email")}
              htmlFor="email"
              hint={t("profile.account.emailReadOnly")}
              className="sm:col-span-2"
            >
              <WarmInput id="email" value={user.email} disabled readOnly />
            </FieldWrap>
          </section>
        </div>

        {/* Sticky-feeling action bar */}
        <div
          className="flex items-center justify-between gap-3 border-t px-6 py-4 sm:px-8"
          style={{
            borderColor: "var(--color-warm-border)",
            background: "oklch(98% 0.005 84 / 0.6)",
          }}
        >
          <p className="text-xs text-muted-foreground">
            {dirty
              ? t("profile.account.unsaved")
              : t("profile.account.allSaved")}
          </p>
          <Button
            type="submit"
            disabled={!dirty || isSaving || isUploading}
            className="rounded-full px-6"
          >
            {isSaving ? (
              <CircleNotch className="size-4 animate-spin" />
            ) : (
              <Check className="size-4" />
            )}
            {t("profile.account.save")}
          </Button>
        </div>
      </WarmCard>
    </form>
  );
}

// ————————————————————————————————————————————————————————————————
// Shared atoms — keep consistent warm aesthetic

export function WarmCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="overflow-hidden rounded-3xl bg-white"
      style={{
        border: "1px solid var(--color-warm-border)",
        boxShadow: "0 1px 2px oklch(0% 0 0 / 0.02)",
      }}
    >
      {children}
    </div>
  );
}

function Divider() {
  return (
    <div
      className="h-px"
      style={{
        background:
          "linear-gradient(to right, transparent, var(--color-warm-border), transparent)",
      }}
    />
  );
}

export function FieldWrap({
  label,
  htmlFor,
  hint,
  className,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label
        htmlFor={htmlFor}
        className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.1em]"
        style={{ color: "var(--color-brand-muted)" }}
      >
        {label}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function WarmInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const { style: styleOverride, ...rest } = props;
  return (
    <input
      {...rest}
      className={
        "w-full rounded-xl bg-white px-4 py-2.5 text-sm text-foreground outline-none transition-all duration-200 placeholder:text-muted-foreground/60 disabled:bg-[oklch(98%_0.005_84)] disabled:text-muted-foreground " +
        (props.className ?? "")
      }
      style={{
        border: "1px solid var(--color-warm-border)",
        boxShadow: "inset 0 1px 2px oklch(0% 0 0 / 0.02)",
        ...styleOverride,
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "var(--color-brand)";
        e.currentTarget.style.boxShadow =
          "0 0 0 3px var(--color-brand-bg), inset 0 1px 2px oklch(0% 0 0 / 0.02)";
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = "var(--color-warm-border)";
        e.currentTarget.style.boxShadow = "inset 0 1px 2px oklch(0% 0 0 / 0.02)";
        props.onBlur?.(e);
      }}
    />
  );
}
